import { makeStyles } from '@material-ui/styles'
import UserRolesTableField from 'account/components/userManagement/tenants/UserRolesTableField'
import { listUserTenants } from 'account/components/userManagement/tenants/new-actions'
import { listUsers } from 'account/components/userManagement/users/new-actions'
import { usersSelector } from 'account/components/userManagement/users/selectors'
import ApiClient from 'api-client/ApiClient'
import VolumeTypesPicklist from 'app/plugins/openstack/components/storage/volume-types/VolumeTypesPicklist'
import { setDefaultVolumeTypeTenant } from 'app/plugins/openstack/components/storage/volume-types/actions'
import Progress from 'core/components/progress/Progress'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { isSystemAdminUser } from 'core/utils/helpers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { pathStr } from 'utils/fp'
import DomainNameTextField from '../domains/DomainNameTextField'
import { getTenantRoleAssignments, updateTenant } from './new-actions'
import DataKeys from 'k8s/DataKeys'
import store from 'app/store'
import { cacheActions } from 'core/caching/cacheReducers'
import VmStorageTypePicklist, { VmStorageOptions } from './VmStorageTypePicklist'

const useStyles = makeStyles<Theme>((theme) => ({}))

const { dispatch } = store
const { cinder } = ApiClient.getInstance()
interface Props {
  rows: any[]
  onClose: () => void
}

export default function EditTenantModal({ rows: [tenant], onClose }: Props) {
  const defaultParams = {
    name: '',
    description: '',
    roleAssignments: {},
    prevRoleAssignments: {},
    volumeType: '',
    storageType: VmStorageOptions.ephemeral_and_volume,
  }
  const [roleAssignments, setRoleAssignments] = useState([])
  const [loadingRoleAssignments, setLoadingRoleAssignments] = useState(true)
  const [dataLoaded, setDataLoaded] = useState(false)
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const [defaultVolumeType, setDefaultVolumeType] = useState(null)
  const [loadingDefaultVolumeType, setLoadingDefaultVolumeType] = useState(true)

  const { loading: loadingUsers, reload: reloadUsers } = useListAction(listUsers, {
    params: {
      domainId: tenant?.domain_id || '',
    },
  })
  const users = useSelector(usersSelector)
  const { reload: reloadTenants } = useListAction(listUserTenants)

  const { update, updating, error, reset } = useUpdateAction(updateTenant)
  const {
    update: setDefaultFn,
    updating: updatingTenantVolumeType,
    error: tenantVolumeTypeError,
    reset: resetTenantVolumeType,
  } = useUpdateAction(setDefaultVolumeTypeTenant)

  // load default volume type
  useEffect(() => {
    const loadDefaultVolumeType = async () => {
      try {
        const response = await cinder.getDefaultTenantVolumeType(tenant.id)
        setDefaultVolumeType(response)
      } catch (e) {
        setDefaultVolumeType({
          volume_type_id: '',
        })
      } finally {
        setLoadingDefaultVolumeType(false)
      }
    }
    loadDefaultVolumeType()
  }, [])

  useEffect(() => {
    const makeCalls = async () => {
      // Reload users only for system admins as the domain_id may be different for each tenant
      if (isSystemAdminUser()) reloadUsers(true)
      const response = await getTenantRoleAssignments({ tenantId: tenant.id })
      setRoleAssignments(response)
      setLoadingRoleAssignments(false)
    }
    makeCalls()
  }, [])

  useEffect(() => {
    if (loadingRoleAssignments) {
      return
    }
    const initialParams = {
      id: tenant.id,
      name: tenant.name,
      description: tenant.description || '',
      roleAssignments: roleAssignments.reduce(
        (acc, roleAssignment) => ({
          ...acc,
          [pathStr('user.id', roleAssignment)]: pathStr('role.id', roleAssignment),
        }),
        {},
      ),
      prevRoleAssignments: roleAssignments.reduce(
        (acc, roleAssignment) => ({
          ...acc,
          [pathStr('user.id', roleAssignment)]: pathStr('role.id', roleAssignment),
        }),
        {},
      ),
      storageType:
        tenant.tags.find((tag) => tag.startsWith('vm_storage_type='))?.split('=')[1] ||
        VmStorageOptions.ephemeral_and_volume,
    }
    updateParams(initialParams)
    setDataLoaded(true)
  }, [tenant, roleAssignments, loadingRoleAssignments])

  const preSelectedRoleAssignments = useMemo(
    () =>
      roleAssignments.reduce(
        (acc, roleAssignment) => ({
          ...acc,
          [pathStr('user.id', roleAssignment)]: pathStr('role.id', roleAssignment),
        }),
        {},
      ),
    [roleAssignments],
  )

  const initialContext = useMemo(() => {
    const tags = tenant.tags || []
    const storageType = tags.find((tag) => tag.startsWith('vm_storage_type='))?.split('=')[1]
    return {
      id: tenant.id,
      name: tenant.name,
      description: tenant.description || '',
      roleAssignments: preSelectedRoleAssignments,
      prevRoleAssignments: roleAssignments.reduce(
        (acc, roleAssignment) => ({
          ...acc,
          [pathStr('user.id', roleAssignment)]: pathStr('role.id', roleAssignment),
        }),
        {},
      ),
      storageType: storageType || VmStorageOptions.ephemeral_and_volume,
    }
  }, [tenant, roleAssignments, loadingRoleAssignments])

  const loadingSomething =
    loadingUsers || loadingRoleAssignments || !dataLoaded || loadingDefaultVolumeType

  const volumeTypeFilterFn = useCallback(
    (options) => options.filter((option) => option?.label && option?.label !== '__DEFAULT__'),
    [],
  )

  const submitForm = useCallback(async () => {
    const tags = tenant.tags || []
    const vmStorageTypeTag = `vm_storage_type=${params.storageType}`
    const filteredTags = tags.filter((tag) => !tag.startsWith('vm_storage_type='))
    const body = {
      id: tenant?.id,
      name: params.name,
      description: params.description,
      roleAssignments: params.roleAssignments,
      prevRoleAssignments: params.prevRoleAssignments,
      tags: [...filteredTags, vmStorageTypeTag],
    }
    const { success } = await update(body)
    if (success) {
      if (params.volumeType) {
        const { success: volumeTypeSuccess } = await setDefaultFn({
          id: tenant?.id,
          body: { volume_type: params.volumeType },
        })
        if (volumeTypeSuccess) {
          // Clear the tenant default volume type cache to ensure
          // all components (like VolumeTypesPicklist) refetch and get the updated default
          dispatch(cacheActions.clearCache({ cacheKey: DataKeys.TenantDefaultVolumeTypes }))
          resetTenantVolumeType()
        } else {
          return
        }
      }
      reloadTenants(true, true)
      handleClose()
    }
  }, [tenant?.id, params])

  const handleClose = () => {
    setParams(defaultParams)
    setDataLoaded(false)
    setLoadingRoleAssignments(true)
    setRoleAssignments([])
    reset()
    onClose()
  }

  return (
    <ModalForm
      title={`Edit Tenant`}
      onSubmit={submitForm}
      onClose={handleClose}
      loading={loadingSomething}
      submitting={updating || updatingTenantVolumeType}
      error={error || tenantVolumeTypeError}
      initialValues={initialContext}
      submitTitle={`Update Tenant`}
      open
    >
      <>
        <FormFieldSection title="Tenant Settings">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            disabled={tenant?.name === 'service'}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
            disabled={tenant?.name === 'service'}
          />
          <DomainNameTextField domainId={tenant?.domain_id} isReadOnly={true} />
          <PicklistField
            DropdownComponent={VolumeTypesPicklist}
            id="volumeType"
            onChange={getParamsUpdater('volumeType')}
            value={params.volumeType}
            filterFn={volumeTypeFilterFn}
            label="Volume Type"
            overrideDefaultVolumeType={defaultVolumeType}
          />
          <VmStorageTypePicklist
            value={params.storageType}
            onChange={getParamsUpdater('storageType')}
            tooltip="This change only affects new VMs. Existing VMs will not be affected."
          />
        </FormFieldSection>
        <FormFieldSection title="User Assignment">
          <Progress
            renderContentOnMount={!loadingSomething}
            loading={loadingSomething}
            message={'Loading Users...'}
          >
            <UserRolesTableField
              id="roleAssignments"
              users={users}
              onChange={getParamsUpdater('roleAssignments')}
              value={params.roleAssignments}
              preSelectedUserIds={preSelectedRoleAssignments}
            />
          </Progress>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
